import { useMutation } from "@tanstack/react-query"
import { createFileRoute, Link, redirect, useNavigate } from "@tanstack/react-router"
import type React from "react"
import { useState } from "react"
import { z } from "zod"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { useToast } from "@/hooks/use-toast"
import { authClient } from "../lib/auth/authClient"

export const Route = createFileRoute("/login")({
  component: Login,
  validateSearch: z.object({ redirect: z.string().optional() }),
  beforeLoad: async () => {
    const { data: session } = await authClient.getSession()
    if (session) {
      throw redirect({ to: "/workout" })
    }
    return null
  },
})

function GoogleIcon() {
  return (
    <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1Z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.65l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z"
      />
      <path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84Z" />
      <path
        fill="#EA4335"
        d="M12 4.75c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 1.39 14.97.4 12 .4A11 11 0 0 0 2.18 6.06l3.66 2.84C6.71 6.68 9.14 4.75 12 4.75Z"
      />
    </svg>
  )
}

function Login() {
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [otpEmail, setOtpEmail] = useState("")
  const [otp, setOtp] = useState("")
  const [otpSent, setOtpSent] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const navigate = useNavigate()
  const { toast } = useToast()
  const { redirect: redirectTo } = Route.useSearch()

  // Email + Password login
  const { mutateAsync: loginWithPassword } = useMutation({
    mutationFn: async () => {
      const result = await authClient.signIn.email({
        email,
        password,
        callbackURL: redirectTo ?? "/workout",
      })
      if (result.error) {
        throw new Error(result.error.message ?? "Login failed")
      }
      return result
    },
    onError: (error) => {
      toast({
        title: "Login failed",
        description: error.message ?? "Invalid email or password",
        variant: "destructive",
      })
    },
  })

  // Send OTP
  const { mutateAsync: sendOtp } = useMutation({
    mutationFn: async () => {
      const result = await authClient.emailOtp.sendVerificationOtp({
        email: otpEmail,
        type: "sign-in",
      })
      if (result.error) {
        throw new Error(result.error.message ?? "Failed to send OTP")
      }
      return result
    },
    onError: (error) => {
      toast({
        title: "Failed to send code",
        description: error.message,
        variant: "destructive",
      })
    },
  })

  // Verify OTP
  const { mutateAsync: verifyOtp } = useMutation({
    mutationFn: async () => {
      const result = await authClient.signIn.emailOtp({
        email: otpEmail,
        otp,
      })
      if (result.error) {
        throw new Error(result.error.message ?? "Invalid code")
      }
      return result
    },
    onError: (error) => {
      toast({
        title: "Invalid code",
        description: error.message,
        variant: "destructive",
      })
    },
  })

  const handleGoogleSignIn = async () => {
    setIsLoading(true)
    try {
      await authClient.signIn.social({
        provider: "google",
        callbackURL: new URL(redirectTo ?? "/workout", window.location.origin).toString(),
      })
    } catch (error) {
      toast({
        title: "Google sign-in failed",
        description: error instanceof Error ? error.message : "Could not start Google sign-in",
        variant: "destructive",
      })
      setIsLoading(false)
    }
  }

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email || !password) {
      toast({ title: "Missing fields", description: "Please fill in all fields", variant: "destructive" })
      return
    }
    setIsLoading(true)
    try {
      await loginWithPassword()
      toast({ title: "Login successful", description: "Welcome back!" })
      navigate({ to: (redirectTo as any) ?? "/workout" })
    } catch {
      // error handled in mutation
    } finally {
      setIsLoading(false)
    }
  }

  const handleSendOtp = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!otpEmail) {
      toast({ title: "Missing email", description: "Please enter your email", variant: "destructive" })
      return
    }
    setIsLoading(true)
    try {
      await sendOtp()
      setOtpSent(true)
      toast({ title: "Code sent", description: "Check your email for the login code" })
    } catch {
      // error handled in mutation
    } finally {
      setIsLoading(false)
    }
  }

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!otp) {
      toast({ title: "Missing code", description: "Please enter the code from your email", variant: "destructive" })
      return
    }
    setIsLoading(true)
    try {
      await verifyOtp()
      toast({ title: "Login successful", description: "Welcome back!" })
      navigate({ to: (redirectTo as any) ?? "/workout" })
    } catch {
      // error handled in mutation
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-2 text-center">
          <div className="flex justify-center">
            <img src="/openiron.png" alt="OpenIron Logo" className="h-16 w-16" />
          </div>
          <CardTitle className="text-2xl">OpenIron</CardTitle>
          <CardDescription>Sign in to your account</CardDescription>
        </CardHeader>

        <CardContent className="space-y-4">
          <Button type="button" variant="outline" className="w-full" disabled={isLoading} onClick={handleGoogleSignIn}>
            <GoogleIcon />
            Continue with Google
          </Button>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-2 text-muted-foreground">Or continue with</span>
            </div>
          </div>

          <Tabs defaultValue="otp">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="otp">OTP</TabsTrigger>
              <TabsTrigger value="password">Password</TabsTrigger>
            </TabsList>

            <TabsContent value="otp" className="mt-4">
              {!otpSent ? (
                <form onSubmit={handleSendOtp} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="otp-email">Email</Label>
                    <Input
                      id="otp-email"
                      type="email"
                      value={otpEmail}
                      onChange={(e) => setOtpEmail(e.target.value)}
                      placeholder="your@email.com"
                      required
                      autoComplete="email"
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={isLoading}>
                    {isLoading ? "Sending..." : "Send Login Code"}
                  </Button>
                </form>
              ) : (
                <form onSubmit={handleVerifyOtp} className="space-y-4">
                  <p className="text-muted-foreground text-sm">
                    We sent a 6-digit code to <strong>{otpEmail}</strong>
                  </p>
                  <div className="space-y-2">
                    <Label htmlFor="otp-code">Login Code</Label>
                    <Input
                      id="otp-code"
                      type="text"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value)}
                      placeholder="000000"
                      maxLength={6}
                      required
                      autoComplete="one-time-code"
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={isLoading}>
                    {isLoading ? "Verifying..." : "Verify Code"}
                  </Button>
                  <Button type="button" variant="link" className="w-full" onClick={() => setOtpSent(false)}>
                    Resend code
                  </Button>
                </form>
              )}
            </TabsContent>

            <TabsContent value="password" className="mt-4">
              <form onSubmit={handlePasswordSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    required
                    autoComplete="email"
                  />
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password">Password</Label>
                    <Link to="/forgotPassword" className="text-primary text-xs hover:underline">
                      Forgot password?
                    </Link>
                  </div>
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    autoComplete="current-password"
                  />
                </div>
                <Button type="submit" className="w-full" disabled={isLoading}>
                  {isLoading ? "Signing in..." : "Sign In"}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </CardContent>

        <CardFooter className="flex-col space-y-4">
          <p className="text-center text-muted-foreground text-sm">
            Don't have an account?{" "}
            <Link to="/register" className="text-primary hover:underline">
              Sign Up
            </Link>
          </p>
          <Link to="/privacy">
            <Button type="button" variant="outline" className="w-full">
              View Privacy Policy
            </Button>
          </Link>
        </CardFooter>
      </Card>
    </div>
  )
}
